<template>
  <environment-banner v-if="isNotProduction" :environment="environment" :branch="branch" />
  <div class="wrapper position-relative">
    <div class="enlarge-trigger position-absolute d-md-none" @click="enlargeElement" v-if="sidebarActive === true">
      <div class="bg-primary rounded-5 w-75">
        <i class="mdi mdi-chevron-double-left mdi-18px text-white enlarge-icon"></i>
      </div>
    </div>
    <navbar>
      <template v-slot:logo>
        <router-link
          :to="{ name: 'retailer.dashboard' }"
          class="logo text-center text-decoration-none"
          @click="handleLogoClick"
        >
          <BaseLogo item-class="logo-lg" alignment="horizontal" />
          <BaseLogo
            item-class="logo-sm"
            alignment="single"
            :height="24"
            :class="{ 'test-environment-style': isNotProduction }"
          />
        </router-link>
      </template>
      <template v-slot:sales>
        <make-sell-component />
      </template>
      <template v-slot:items>
        <list :listItems="listItemsWithBadgeCount" item-class="side-nav side-nav-light" :header="true" :user="user" />
      </template>
    </navbar>
    <div class="content-page position-relative" id="contentPage">
      <div class="content">
        <!-- Start Topbar Content-->
        <div class="navbar-custom shadow-sm px-1">
          <PanelTopbarContent />
        </div>
        <div
          class="d-flex border-bottom border-light border-opacity-50 mx--1 bg-white p-2"
          v-if="$country.is('tr') && lastOrders?.length > 0 && $router.currentRoute.value.name === 'retailer.dashboard'"
        >
          <div class="font-18 fw-semibold d-flex align-items-center text-nowrap text-secondary me-2">
            <tooltip :content="$t('views.layouts.last_orders_info')" placement="top-start">
              <i class="mdi mdi-information-outline mdi-24px me-1"></i>
            </tooltip>
            {{ $t('views.layouts.last_orders') }}
          </div>
          <vue3-marquee :pause-on-hover="true" :clone="true" :duration="lastOrders.length * 3">
            <div
              v-for="order in lastOrders"
              :key="order.id"
              class="d-flex align-items-center text-nowrap text-secondary bg-marquee-item px-2 py-1 rounded ms-2"
            >
              <span class="font-16 fw-500">{{ order.product.name }}</span
              >:
              <span class="fw-semibold font-16 text-muted ms-1"
                >{{ money.ofMinor(order.cash_unit_price).toFormat('0[,]000') }} {{ currencySymbol }}/{{
                  order.product_unit.unit_name_with_rate_to_base_unit
                }}</span
              >
            </div>
          </vue3-marquee>
        </div>
        <!-- Finish Topbar Content-->
        <div class="container position-relative">
          <BaseBreadcrumb v-if="!mobilePricePage" />
          <notification-bar
            v-for="notification in getSystemWideNotifications"
            :notification="notification"
            :key="notification.id"
          />
          <div class="row px-2">
            <div class="col-12 px-0" :class="{ 'px-0': mobilePricePage }">
              <router-view></router-view>
            </div>
          </div>
        </div>
      </div>
      <footer class="footer" v-if="!mobilePricePage">
        <div class="container-fluid">
          <div class="row">
            <div class="col-md-6">
              {{ $t('views.layouts.tarfin_company_mark', [new Date().getFullYear()]) }}
            </div>
            <div class="col-md-6">
              <div class="text-md-end footer-links d-none d-md-block">
                <a :href="$t('views.layouts.about_us_link')" target="_blank" class="text-decoration-none">{{
                  $t('views.layouts.about_us')
                }}</a>
                <a :href="$t('views.layouts.privacy_policy_link')" target="_blank" class="text-decoration-none">{{
                  $t('views.layouts.privacy_agreement')
                }}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <notification-permission-modal v-if="$country.is('tr') && isShowNotificationModal" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import * as Sentry from '@sentry/vue'

import _ from 'lodash'

import { Vue3Marquee } from 'vue3-marquee'

import store from '@/store/store.js'

import country from '@/utilities/country.js'
import { isAnyMobileDevice } from '@/utilities/helpers.js'

import money from '@/utilities/money.js'
import sessionLockManager from '@/utilities/sessionLockManager.js'

import { NOTIFICATION_TYPES } from '@/constants/constants.js'
import { RETAILER_USER_ROLE } from '@/constants/retailerUserRole.js'

import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import BaseLogo from '@/components/BaseLogo.vue'
import EnvironmentBanner from '@/components/EnvironmentBanner/index.vue'

import List from '@/components/List.vue'
import Navbar from '@/components/Navbar.vue'
import NotificationBar from '@/components/NotificationBar.vue'
import NotificationPermissionModal from '@/components/NotificationPermissionModal.vue'
import MakeSellComponent from '@/components/retailer/navbar/MakeSellComponent.vue'
import Tooltip from '@/components/Tooltip.vue'
import PanelTopbarContent from '@/views/layouts/PanelTopbarContent.vue'

async function getUser() {
  await store.dispatch('retailerAuth/getUser').then(user => {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: `${user.firstname} ${user.lastname}`
    })
  })
}

export default {
  name: 'admin',
  components: {
    Tooltip,
    Vue3Marquee,
    NotificationBar,
    EnvironmentBanner,
    NotificationPermissionModal,
    Navbar,
    PanelTopbarContent,
    List,
    BaseBreadcrumb,
    BaseLogo,
    MakeSellComponent
  },
  data() {
    return {
      listItems: [
        {
          text: this.$t('views.layouts.summary'),
          icon: 'mdi mdi-view-dashboard',
          name: 'retailer.dashboard'
        },
        {
          text: this.$t('views.layouts.sales'),
          icon: 'mdi mdi-briefcase',
          name: 'retailer.orders.index'
        },
        {
          text: this.$t('views.layouts.tarfin_pro'),
          icon: 'mdi mdi-offer',
          name: 'retailer.tarfin-pro',
          show: () => false,
          child: [
            {
              text: this.$t('views.layouts.pro_price_list'),
              name: 'retailer.tarfin-pro'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_request_quotation'),
              name: 'retailer.tarfin-pro-requests.create'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_requests'),
              name: 'retailer.tarfin-pro-requests.index'
            }
          ]
        },
        {
          text: this.$t('views.layouts.farmers'),
          icon: 'mdi mdi-tractor',
          name: 'retailer.farmers.index'
        },
        {
          text: this.$t('views.layouts.purchase_requests'),
          icon: 'mdi mdi-cash',
          name: 'retailer.purchase-offers.index',
          show: () => {
            return this.$country.isNot('ro')
          }
        },
        {
          text: this.$t('views.layouts.price'),
          icon: 'mdi mdi-cash',
          name: 'retailer.prices.index',
          show: () => {
            return this.$country.isNot('ro')
          }
        },
        {
          text: this.$t('views.layouts.current'),
          icon: 'mdi mdi-clipboard-text',
          name: 'retailer.current-account.activities.index',
          permission: 'owner',
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text: this.$t('views.layouts.sub_retailer'),
          icon: 'mdi mdi-store',
          name: 'retailer.sub-retailers.index',
          permission: 'owner',
          show: () => {
            return this.$country.isNot('ro')
          },
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text: this.$t('views.layouts.users'),
          icon: 'mdi mdi-account-multiple',
          name: 'retailer.users.index',
          permission: 'owner'
        },
        {
          text: this.$t('views.layouts.revelant_sales_team'),
          icon: 'mdi mdi-account-group',
          name: 'retailer.support.index'
        },
        {
          text: this.$t('views.layouts.brands'),
          icon: 'mdi mdi-tag-multiple',
          name: 'retailer.brands.index',
          show: () => {
            return this.$country.isNot('ro')
          }
        },
        {
          text: this.$t('views.layouts.stocks'),
          icon: 'mdi mdi-view-dashboard',
          name: 'retailer.stockInputs.show',
          show: () => false
        },
        {
          text: this.$t('views.layouts.goals'),
          icon: 'mdi mdi-trophy-outline',
          name: 'retailer.goals.index',
          permission: 'owner',
          show: () => {
            return this.$country.isNot('ro')
          },
          roles: [RETAILER_USER_ROLE.SUPER]
        },
        {
          text: this.$t('views.layouts.tarfin_company_info'),
          icon: 'mdi mdi-domain',
          name: 'retailer.tarfin.company.info'
        },
        {
          text: this.$t('views.layouts.faq_title'),
          icon: 'mdi mdi-file-document-outline',
          name: 'retailer.ro.faq.index',
          show: () => {
            return this.$country.isNot('tr')
          }
        },
        {
          text: this.$t('views.layouts.user_guide'),
          icon: 'mdi mdi-file-document-outline',
          name: 'retailer.faq.index',
          show: () => {
            return this.$country.isNot('ro')
          }
        }
      ],
      isAndroid: isAnyMobileDevice(),
      isPricesPage: false,
      environment: 'dev',
      branch: ''
    }
  },
  computed: {
    money() {
      return money
    },
    ...mapState('retailerAuth', ['user']),
    ...mapState('sideMenu', ['sidebarActive']),
    ...mapGetters('notification', ['getNotificationsByKind']),
    ...mapState('retailerDashboard', ['menuItemsBadgeCount', 'lastOrders']),

    getSystemWideNotifications() {
      return this.getNotificationsByKind(NOTIFICATION_TYPES.BAR)
    },
    mobilePricePage() {
      return this.isPricesPage && this.isAndroid
    },
    isNotProduction() {
      return import.meta.env.MODE !== 'production'
    },
    isShowNotificationModal() {
      return this.$router.currentRoute.value.fullPath !== '/retailer/application/create'
    },
    listItemsWithBadgeCount() {
      return this.listItems.map(item => {
        return {
          ...item,
          badgeCount: this.menuItemsBadgeCount[item.name] ?? 0
        }
      })
    },
    currencySymbol() {
      return import.meta.env.VITE_CURRENCY_SYMBOL
    }
  },

  created() {
    if (this.user) {
      const phone = this.user.phone ? this.user.phone.replace(/\(|\)| /g, '') : null
      const env = import.meta.env.MODE
      if (env === 'production') {
        let apiKey = ''
        if (this.$country.is('tr')) {
          apiKey = '9sZcpeqiHefkPOSHkOoDAbkpo9MuWMb2UeiMedqh'
        }
        if (this.$country.is('ro')) {
          apiKey = 'fZybEuCmoOtnXvvTtzSXMPSmrREentYiBo98EF9V'
        }

        window.InfosetChat('boot', {
          widget: { apiKey: apiKey },
          visitor: {
            id: this.user.retailer.id,
            firstName: this.user.firstname,
            lastName: this.user.lastname,
            email: this.user.email,
            phone,
            customFields: {
              retailerName: this.user.retailer.name,
              retailerId: this.user.retailer.id,
              region: this.user.retailer.region_manager.region,
              country: this.$country.get()
            }
          }
        })
      }
    }

    if (this.$route.name === 'retailer.prices.index') {
      this.isPricesPage = true
    }
    const route = _.find(this.listItems, { name: this.$route.name })
    route?.routeEnter?.()
  },
  methods: {
    handleLogoClick() {
      store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', 'retailer.dashboard')
    },
    toggleBodyClass(method, bodyClass) {
      const el = document.body

      if (method === 'add') {
        el.classList.add(bodyClass)
      } else {
        el.classList.remove(bodyClass)
      }
    },
    enlargeElement() {
      document.body.className = 'enlarged'
    },
    determineEnvironment(hostname) {
      if (import.meta.env.MODE === 'production') {
        return
      }

      if (import.meta.env.MODE === 'development') {
        this.environment = 'dev'
        document.querySelector('#favicon').href = '/images/environment/favicon-develop.svg'
        return
      }

      if (hostname.includes('beykoz.tarfin.com') || hostname.includes('bucharest.tarfin.com')) {
        this.environment = 'staging'
        document.querySelector('#favicon').href = '/images/environment/favicon-staging.svg'
        return
      }

      this.environment = 'dynamic'
      document.querySelector('#favicon').href = '/images/environment/favicon-dynamic.svg'
    },
    extractDomainPrefix(hostname) {
      this.branch = hostname.split('.tarfin.com')[0]
    }
  },
  async beforeRouteEnter(to, from, next) {
    store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', to.name)

    await getUser()
    if (country.is('tr')) {
      await store.dispatch('retailerDashboard/getDashboardMenuItemsBadgeCount')
    }
    next()
  },
  async beforeRouteUpdate(to, from, next) {
    await getUser()
    if (country.is('tr') && sessionLockManager.lock('retailerDashboard.getDashboardMenuItemsBadgeCount', 1)) {
      await store.dispatch('retailerDashboard/getDashboardMenuItemsBadgeCount')
    }
    next()
  },
  mounted() {
    let leftBarCompactMode = null
    if (localStorage.getItem('sidebarOpened') === '0') {
      leftBarCompactMode = 'condensed'
    } else if (localStorage.getItem('sidebarOpened') === '1') {
      leftBarCompactMode = 'scrollable'
    } else {
      leftBarCompactMode = 'scrollable'
    }
    document.body.setAttribute('data-leftbar-compact-mode', leftBarCompactMode)
    document.body.setAttribute('data-leftbar-theme', 'light')

    // this.toggleBodyClass('add', 'enlarged')

    const hostname = window.location.hostname
    this.determineEnvironment(hostname)
    this.extractDomainPrefix(hostname)

    store.state.sideMenu.sidebarActive = false
  },
  unmounted() {
    document.body.removeAttribute('data-leftbar-compact-mode')
    document.body.removeAttribute('data-leftbar-theme')
    // this.toggleBodyClass('remove', 'enlarged')
  }
}
</script>

<style scoped>
@media (min-width: 1025px) {
  .test-environment-style {
    margin-top: 20px;
  }
}

.mx--1 {
  margin-left: -1rem !important;
  margin-right: -1rem !important;
}

.bg-marquee-item {
  background-color: #f4f5f9 !important;
}

.enlarge-icon {
  padding-left: 15%;
}
</style>
